import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Alert from '../common/Alert.js';
import { contactService } from '../../services/contactService.js';
import { Mail, Pencil, ArrowLeft } from 'lucide-react';
import {
  PageLayout,
  Button,
  DetailCard,
  DetailRow,
  StatusBadge,
} from '../common/index.js';

const ViewContact = () => {
  const { contactId } = useParams();
  const [contact, setContact] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchContact();
  }, [contactId]);

  const fetchContact = async () => {
    try {
      const response = await contactService.getContact(contactId);
      setContact(response.data.data);
    } catch (err) {
      Alert.error('Failed to load contact details');
      console.error(err);
      navigate('/contact');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <PageLayout title="Contact Details" icon={Mail}>
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand mx-auto mb-4" />
            <p className="text-gray-600">Loading contact details...</p>
          </div>
        </div>
      </PageLayout>
    );
  }

  if (!contact) return null;

  return (
    <PageLayout
      title="Contact Details"
      icon={Mail}
      actions={
        <>
          {!contact.reply ? (
            <Button icon={Pencil} onClick={() => navigate(`/contact/edit/${contactId}`)}>
              Send Reply
            </Button>
          ) : (
            <Button variant="secondary" onClick={() => navigate(`/contact/edit/${contactId}`)}>
              View Reply Details
            </Button>
          )}
          <Button variant="outline" icon={ArrowLeft} onClick={() => navigate('/contact')}>
            Back to Contacts
          </Button>
        </>
      }
    >
      <div className="bg-primary rounded-card p-4 mb-4 flex flex-wrap items-center gap-3">
        <h2 className="text-lg font-semibold text-secondary">{contact.full_name}</h2>
        <StatusBadge status={contact.reply ? 'completed' : 'pending'} label={contact.reply ? 'Replied' : 'Pending'} />
        <p className="text-sm text-gray-600 w-full">{contact.email}</p>
      </div>

      <div className="space-y-4">
        <DetailCard title="Contact Information">
          <DetailRow label="Contact ID" value={contact.id} />
          <DetailRow label="Full Name" value={contact.full_name} />
          <DetailRow label="First Name" value={contact.first_name} />
          <DetailRow label="Last Name" value={contact.last_name} />
          <DetailRow label="Email Address" value={contact.email} />
          <DetailRow label="Phone Number" value={contact.phone} />
          <DetailRow label="Location" value={contact.location || 'N/A'} />
        </DetailCard>

        <DetailCard title="Message Details">
          <DetailRow label="Subject" value={contact.subject} />
          <DetailRow label="Message" value={<p className="whitespace-pre-wrap">{contact.message}</p>} />
        </DetailCard>

        {contact.reply && (
          <DetailCard title="Reply Information">
            <DetailRow label="Reply Message" value={<p className="whitespace-pre-wrap">{contact.reply}</p>} />
            <DetailRow label="Replied By" value={contact.replied_by_name || 'N/A'} />
            <DetailRow label="Replied At" value={formatDate(contact.replied_at)} />
          </DetailCard>
        )}

        <DetailCard title="System Information">
          <DetailRow label="Status" value={contact.reply ? 'Replied' : 'Pending Reply'} />
          <DetailRow label="Date Received" value={formatDate(contact.created_at)} />
          <DetailRow label="Last Updated" value={formatDate(contact.updated_at)} />
        </DetailCard>
      </div>
    </PageLayout>
  );
};

export default ViewContact;
